/* eslint-disable react/jsx-key */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/destructuring-assignment */
import React from 'react';
import 'react-table-6/react-table.css';
import {
  IoMdCheckmark as Check,
  IoMdClose as Remove
} from 'react-icons/io';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { fieldWidths } from 'shared/ui/Table';
import FlagIcon from 'Common/Components/FlagIcon';
import DateFormatter from 'Common/Components/DateFormatter';
import Link from 'Common/Components/Link/styled';
import { Impact, GenericCell, Confirm, Severity, Expander, SeverityHeader, CVE, CWE, Risk, DraggableCheckColum, StatusCell } from 'Screens/Contextualization/Manage/components/ManageTable/components/Table/columns';
import { CVSS } from 'Screens/Contextualization/Manage/components/ManageTable/components/Table/columns/CVSS';
import StatusTraceCell from 'Screens/Contextualization/Manage/components/ManageTable/components/Table/columns/StatusTraceCell';
import { TOOL_NAME as TOOL_NAME_SOLAR_WINDS } from 'store/Settings/solarWindsActions';
import { TOOL_NAME as TOOL_NAME_GITLAB } from 'store/Settings/gitLabActions';
import { TOOL_NAME as TOOL_NAME_SERVICE_NOW } from 'store/Settings/serviceNowActions';
import { TOOL_NAME as TOOL_NAME_SERVICE_DESK } from 'store/Settings/serviceDesk/actions';
import CheckHeaderVulnsGeneral from './components/CheckHeaderVulnsGeneral';
import CheckColumnVulnsGeneral from './components/CheckColumnVulnsGeneral';
import NameCellVulnsGeneral from './components/NameCellVulnsGeneral';
import { TagCell } from './components/TagCell';
import assetItemRowText from 'shared/ui/Table/presets';

/**
 * Vulns General table (all workspaces) and its group-by sub table.
 *
 * ctx:
 * - `isPivot` {boolean}: true for the main table, false for the group-by sub table
 * - `customFields` {Object[]}: custom attributes (`field_name`, `field_display_name`)
 * - `showVulnDetail` {boolean}
 * - `currentBreakpoint` {string}
 * - `groupBy` {string}: group by field ('' / undefined when not grouping)
 * - `fields` {Object}: column preferences (`selectVulnsGeneralFields`)
 * - `isFetching` {boolean}
 * - `isFetchingExpandedData` {boolean}
 * - `hasGroupedData` {boolean}: the expanded group already has its rows (sub table only)
 * - `isAllowedByLicense` {boolean}
 * - `showQuickPlanner` {boolean}
 * - `theme` {Object}: `themeStyles`
 *
 * Ported as is from the legacy `getColumns`, bugs included (e.g. tags `minWidth` reads
 * `maxWidth`, workspace is always shown while not grouping, evidence reads a field that
 * `selectVulnsGeneralFields` excludes).
 */

const isDetailExtended = ({ showVulnDetail, currentBreakpoint }) => (showVulnDetail && currentBreakpoint === 'second');

const getShowSkeleton = ({ isFetching, isFetchingExpandedData, isPivot, hasGroupedData }) => isFetching || (isFetchingExpandedData && !isPivot && !hasGroupedData);

const notGrouped = ({ groupBy }) => !groupBy;

const orderOf = (fieldKey) => ({ fields }) => fields[fieldKey]?.order;

const minWdProps = (fieldKey) => (rowInfo, { fields }) => ({ className: `min-wd-${fields[fieldKey]?.defaultMin}` });

const minWdColorProps = (fieldKey) => (rowInfo, { fields, theme }) => ({
  className: `min-wd-${fields[fieldKey]?.defaultMin}`,
  style: { color: theme.text.tableVulnName }
});

const genericCell = (attrib, extraProps = {}) => function renderGenericCell (rowInfo, ctx) {
  return <GenericCell showSkeleton={ getShowSkeleton(ctx) } rowInfo={ rowInfo } attrib={ attrib } { ...extraProps } />;
};

const baseColumns = [
  {
    headerStyle: { display: 'none' },
    id: 'expander',
    visible: ({ groupBy, isPivot }) => !!(groupBy && isPivot),
    sortable: false,
    filterable: false,
    maxWidth: 50,
    minWidth: 50,
    order: -1,
    style: ({ isFetching }) => ({ display: isFetching ? 'none' : '' }),
    expander: ({ isExpanded, original }, { isFetching, groupBy }) => <Expander isFetching={ isFetching } isExpanded={ !!isExpanded } original={ original } group_by={ groupBy } />
  }, {
    header: (info, { groupBy, isPivot }) => ((!groupBy || !isPivot) && <CheckHeaderVulnsGeneral />),
    id: 'selected',
    accessor: 'Selected',
    visible: true,
    sortable: false,
    resizable: false,
    maxWidth: 55,
    minWidth: 55,
    headerStyle: { overflow: 'visible' },
    style: { display: 'flex' },
    skeleton: false,
    cell: (rowInfo, ctx) => {
      const { showQuickPlanner, groupBy, isPivot, isFetching, isFetchingExpandedData, hasGroupedData } = ctx;
      return (showQuickPlanner ? <DraggableCheckColum vuln={ rowInfo.original } /> : (!groupBy || !isPivot) && !isFetching && (!isFetchingExpandedData || hasGroupedData) && <CheckColumnVulnsGeneral vuln={ rowInfo.original } showSkeleton={ getShowSkeleton(ctx) } />);
    }
  }, {
    header: () => <SeverityHeader />,
    showSortIcon: false,
    headerStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
    id: 'severity',
    accessor: 'severity',
    visible: ({ fields, groupBy }) => fields.severity?.visible && (!groupBy || groupBy !== 'severity'),
    sortable: notGrouped,
    resizable: false,
    maxWidth: 45,
    minWidth: 45,
    cellProps: () => ({ style: { display: 'flex', justifyContent: 'center', alignItems: 'center' } }),
    cell: (rowInfo, ctx) => (!ctx.isPivot || !ctx.groupBy) && <Severity vuln={ rowInfo.original } />,
    order: orderOf('severity')
  }, {
    header: () => <FlagIcon title="Confirmed / Not Confirmed" theme="allVulns" />,
    showSortIcon: false,
    id: 'confirmed',
    accessor: 'confirmed',
    visible: ({ fields }) => fields.confirmed?.visible,
    sortable: notGrouped,
    resizable: false,
    maxWidth: 45,
    minWidth: 45,
    cell: (rowInfo, ctx) => (!ctx.isPivot || !ctx.groupBy) && <Confirm vuln={ rowInfo.original } entity="vulnsGeneral" />,
    order: orderOf('confirmed')
  }, {
    header: () => <div children="NAME" />,
    id: 'name',
    accessor: 'name',
    visible: ({ fields }) => fields.name?.visible,
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('name', { groupAware: true }),
    // The legacy table compared the row index with react-table's `state.selected`, a key the engine
    // never sets (it always read the 0 default): the first row never gets `column_name`.
    cellProps: (rowInfo, ctx) => {
      const { fields } = ctx;
      const extendFieldName = isDetailExtended(ctx);
      const index = get(rowInfo, 'index', 0);
      if (index !== 0) return ({ className: `column_name min-wd-${fields.name?.defaultMin} ${extendFieldName ? '' : `max-wd-${fields.name?.maxWidth}`}` });
      return ({ className: `min-wd-${fields.name?.defaultMin} ${extendFieldName ? '' : `max-wd-${fields.name?.maxWidth}`}` });
    },
    order: orderOf('name'),
    skeleton: false,
    cell: (rowInfo, ctx) => <NameCellVulnsGeneral showSkeleton={ getShowSkeleton(ctx) } groupBy={ ctx.groupBy } isPivot={ ctx.isPivot } vuln={ rowInfo.original } />
  }, {
    header: () => <div children="WORKSPACE" />,
    id: 'workspace__name',
    accessor: 'workspace_name',
    visible: ({ fields, groupBy, isPivot }) => fields.workspace_name?.visible || !groupBy || (groupBy && isPivot),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('workspace_name', { groupAware: true }),
    order: orderOf('workspace_name'),
    cellProps: minWdProps('workspace_name'),
    skeleton: false,
    cell: genericCell('workspace_name')
  }, {
    header: () => <div children="SERVICE" />,
    id: 'service__name',
    visible: (ctx) => ctx.fields.service__name?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('service__name', { groupAware: true }),
    cell: (rowInfo) => (
      rowInfo.original.service ? `(${rowInfo.original.service.ports}/${rowInfo.original.service.protocol}) ${rowInfo.original.service.name}` : ''
    ),
    cellProps: minWdColorProps('service__name'),
    order: orderOf('service__name')
  }, {
    header: () => <div children="ASSET" />,
    id: 'target',
    accessor: 'asset',
    visible: (ctx) => ctx.fields.asset?.visible && !isDetailExtended(ctx) && ctx.groupBy !== 'cve_instances__name',
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('asset', { groupAware: true }),
    // Legacy compared `rowInfo.index !== state.selected`, always true (the engine never sets it).
    cellProps: (rowInfo, { fields }) => ({ className: `column_target min-wd-${fields.asset?.defaultMin}` }),
    order: orderOf('asset'),
    skeleton: false,
    cell: genericCell('target')
  }, {
    header: () => <div children="RISK SCORE" />,
    id: 'risk',
    accessor: 'risk',
    visible: (ctx) => ctx.fields.risk?.visible && !isDetailExtended(ctx) && ctx.isAllowedByLicense && !ctx.groupBy,
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('risk', { groupAware: true }),
    cellProps: minWdProps('risk'),
    order: orderOf('risk'),
    skeleton: false,
    cell: (rowInfo, ctx) => <Risk showSkeleton={ getShowSkeleton(ctx) } vuln={ rowInfo.original } />
  }, {
    header: () => <div children="CVSS" />,
    id: 'cvss',
    accessor: 'cvss',
    // community fork: CVSS also requires isAllowedByLicense here (see community-overrides/files
    // in professional for the professional/dev fork of this file).
    visible: (ctx) => ctx.fields.cvss?.visible && !isDetailExtended(ctx) && ctx.isAllowedByLicense,
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('cvss', { groupAware: true }),
    order: orderOf('cvss'),
    skeleton: false,
    cell: (rowInfo, ctx) => {
      if (!rowInfo.original.cvss4 && !rowInfo.original.cvss3 && !rowInfo.original.cvss2) return null;
      return <CVSS showSkeleton={ getShowSkeleton(ctx) } vuln={ rowInfo.original } />;
    },
    cellProps: minWdColorProps('cvss')
  }, {
    header: () => <div children="STATUS TRACE" />,
    id: 'status_history',
    accessor: 'status_history',
    visible: (ctx) => ctx.fields.status_history?.visible && !isDetailExtended(ctx) && !ctx.groupBy,
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('status_history', { groupAware: true }),
    order: orderOf('status_history'),
    skeleton: false,
    cell: (rowInfo, ctx) => <StatusTraceCell vuln={ rowInfo.original } showSkeleton={ getShowSkeleton(ctx) } entity="vulnsGeneral" />,
    cellProps: minWdColorProps('status_history')
  }, {
    header: () => <div children="CVE" />,
    id: 'cve',
    accessor: 'cve',
    visible: (ctx) => ctx.fields.cve?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('cve', { groupAware: true }),
    skeleton: false,
    cell: (rowInfo, ctx) => <CVE showSkeleton={ getShowSkeleton(ctx) } vuln={ rowInfo.original } entity="vulnsGeneral" />,
    cellProps: minWdColorProps('cve'),
    order: orderOf('cve')
  }, {
    header: () => <div children="CWE" />,
    id: 'cwe',
    accessor: 'cwe',
    visible: (ctx) => ctx.fields.cwe?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('cwe', { groupAware: true }),
    order: orderOf('cwe'),
    skeleton: false,
    cell: (rowInfo, ctx) => <CWE showSkeleton={ getShowSkeleton(ctx) } vuln={ rowInfo.original } isFetching={ ctx.isFetching } />,
    cellProps: minWdColorProps('cwe')
  }, {
    header: () => <div children="OWASP" />,
    id: 'owasp',
    accessor: 'owasp',
    visible: (ctx) => ctx.fields.owasp?.visible && !isDetailExtended(ctx) && ctx.isAllowedByLicense && !ctx.groupBy,
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('owasp', { groupAware: true }),
    order: orderOf('owasp'),
    skeleton: false,
    cell: (rowInfo) => {
      if (!rowInfo.original.owasp) return <span />;
      return <>{rowInfo.original.owasp.map(el => (<>{el} </>))}</>;
    },
    cellProps: minWdColorProps('owasp')
  }, {
    header: () => <div children="ID" />,
    id: 'id',
    accessor: '_id',
    visible: (ctx) => ctx.fields.id?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('id', { groupAware: true }),
    cellProps: minWdProps('id'),
    order: orderOf('id'),
    skeleton: false,
    cell: genericCell('obj_id')
  }, {
    header: () => <div children="ASSET OS" />,
    id: 'host__os',
    accessor: 'host_os',
    visible: (ctx) => ctx.fields.host__os?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('host__os', { groupAware: true }),
    cellProps: minWdProps('host__os'),
    order: orderOf('host__os'),
    skeleton: false,
    cell: genericCell('host_os')
  }, {
    header: () => <div children="EASE OF RESOLUTION" />,
    id: 'easeofresolution',
    accessor: 'easeofresolution',
    visible: (ctx) => ctx.fields.easeofresolution?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('easeofresolution', { groupAware: true }),
    cellProps: minWdProps('easeofresolution'),
    order: orderOf('easeofresolution'),
    skeleton: false,
    cell: genericCell('easeofresolution', { capitalize: true })
  }, {
    header: () => <div children="WEBSITE" />,
    id: 'website',
    accessor: 'website',
    visible: (ctx) => ctx.fields.website?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('website', { groupAware: true }),
    cellProps: minWdProps('website'),
    order: orderOf('website'),
    skeleton: false,
    cell: genericCell('website')
  }, {
    header: () => <div children="PATH" />,
    id: 'path',
    accessor: 'path',
    visible: (ctx) => ctx.fields.path?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('path', { groupAware: true }),
    cellProps: minWdProps('path'),
    order: orderOf('path'),
    skeleton: false,
    cell: genericCell('path')
  }, {
    header: () => <div children="STATUS CODE" />,
    id: 'status_code',
    accessor: 'status_code',
    visible: (ctx) => ctx.fields.status_code?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('status_code', { groupAware: true }),
    cellProps: minWdProps('status_code'),
    order: orderOf('status_code'),
    skeleton: false,
    cell: genericCell('status_code')
  }, {
    header: () => <div children="HOSTNAMES" />,
    id: 'hostnames',
    accessor: 'hostnames',
    visible: (ctx) => ctx.fields.hostnames?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('hostnames', { groupAware: true }),
    cell: (rowInfo) => {
      const hostnames = get(rowInfo, 'original.hostnames', []);
      return <div className="three-dots">{hostnames.join(', ')}</div>;
    },
    cellProps: minWdColorProps('hostnames'),
    order: orderOf('hostnames')
  }, {
    header: () => <div children="IMPACT" />,
    id: 'impact',
    visible: (ctx) => ctx.fields.impact?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('impact', { groupAware: true }),
    skeleton: false,
    cell: (rowInfo, ctx) => <Impact showSkeleton={ getShowSkeleton(ctx) } rowInfo={ rowInfo } />,
    cellProps: minWdColorProps('impact'),
    order: orderOf('impact')
  }, {
    header: () => <div><FormattedMessage id="manage.table.columns.description" /></div>,
    id: 'description',
    accessor: 'description',
    visible: (ctx) => ctx.fields.description?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('description', { groupAware: true }),
    cellProps: minWdProps('description'),
    order: orderOf('description'),
    skeleton: false,
    cell: genericCell('description')
  }, {
    header: () => <div><FormattedMessage id="manage.table.columns.data" /></div>,
    id: 'data',
    accessor: 'data',
    visible: (ctx) => ctx.fields.data?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('data', { groupAware: true }),
    cellProps: minWdProps('data'),
    order: orderOf('data'),
    skeleton: false,
    cell: genericCell('data')
  }, {
    header: () => <div><FormattedMessage id="manage.table.columns.request" /></div>,
    id: 'request',
    accessor: 'request',
    visible: (ctx) => ctx.fields.request?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('request', { groupAware: true }),
    cellProps: minWdProps('request'),
    order: orderOf('request'),
    skeleton: false,
    cell: genericCell('request')
  }, {
    header: () => <div><FormattedMessage id="manage.table.columns.response" /></div>,
    id: 'response',
    accessor: 'response',
    visible: (ctx) => ctx.fields.response?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('response', { groupAware: true }),
    cellProps: minWdProps('response'),
    order: orderOf('response'),
    skeleton: false,
    cell: genericCell('response')
  }, {
    header: () => <div><FormattedMessage id="manage.table.columns.resolution" /></div>,
    id: 'resolution',
    accessor: 'resolution',
    visible: (ctx) => ctx.fields.resolution?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('resolution', { groupAware: true }),
    cellProps: minWdProps('resolution'),
    order: orderOf('resolution'),
    skeleton: false,
    cell: genericCell('resolution')
  }, {
    header: () => <div children="METHOD" />,
    id: 'method',
    accessor: 'method',
    visible: (ctx) => ctx.fields.method?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('method', { groupAware: true }),
    cellProps: minWdProps('method'),
    order: orderOf('method'),
    skeleton: false,
    cell: genericCell('method')
  }, {
    header: () => <div children="QUERY" />,
    id: 'query',
    accessor: 'query',
    visible: (ctx) => ctx.fields.query?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('query', { groupAware: true }),
    cellProps: minWdProps('query'),
    order: orderOf('query'),
    skeleton: false,
    cell: genericCell('query')
  }, {
    header: () => <div children="EVIDENCE" />,
    id: 'evidence',
    accessor: 'evidence',
    visible: (ctx) => ctx.fields.evidence?.visible && !isDetailExtended(ctx),
    sortable: false,
    resizable: notGrouped,
    ...fieldWidths('evidence', { groupAware: true }),
    cellProps: minWdProps('evidence'),
    order: orderOf('evidence'),
    skeleton: false,
    cell: genericCell('attachments_count')
  }, {
    header: () => <div children="WEB" />,
    id: 'type',
    accessor: 'type',
    visible: (ctx) => ctx.fields.web?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('web', { groupAware: true }),
    cell: (rowInfo, ctx) => {
      if (ctx.groupBy) return null;
      if (rowInfo.original.type === 'VulnerabilityWeb') return <Check />;
      return <Remove />;
    },
    cellProps: minWdColorProps('web'),
    order: orderOf('web')
  }, {
    header: () => <div children="CREATOR TOOL" />,
    id: 'tool',
    accessor: 'metadata.tool',
    visible: (ctx) => ctx.fields.tool?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('tool', { groupAware: true }),
    cellProps: minWdProps('tool'),
    order: orderOf('tool'),
    skeleton: false,
    cell: genericCell('tool')
  }, {
    header: () => <div children="CREATOR COMMAND ID" />,
    id: 'creator_command_id',
    accessor: 'metadata.command_id',
    visible: (ctx) => ctx.fields.creator_command_id?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('creator_command_id', { groupAware: true }),
    cellProps: minWdProps('creator_command_id'),
    order: orderOf('creator_command_id'),
    skeleton: false,
    cell: genericCell('metadata.command_id')
  }, {
    header: () => <div children="EXTERNAL ID" />,
    id: 'external_id',
    accessor: 'external_id',
    visible: (ctx) => ctx.fields.external_id?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('external_id', { groupAware: true }),
    cellProps: minWdProps('external_id'),
    order: orderOf('external_id'),
    skeleton: false,
    cell: genericCell('external_id')
  }, {
    header: () => <div children="Created" />,
    id: 'create_date',
    visible: (ctx) => ctx.fields.create_date?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('create_date', { groupAware: true }),
    cell: (rowInfo) => (rowInfo.original.date ? <DateFormatter date={ rowInfo.original.date } /> : null),
    cellProps: minWdColorProps('create_date'),
    order: orderOf('create_date')
  }, {
    header: () => (
      <div>
        <FormattedMessage id="manage.table.columns.issueTracker" />
      </div>
    ),
    id: 'issuetracker',
    resizable: notGrouped,
    visible: ({ fields }) => fields.issuetracker?.visible,
    ...fieldWidths('issuetracker', { groupAware: true }),
    skeleton: false,
    cell: (rowInfo) => {
      if (isEmpty(rowInfo.original.issuetracker)) return null;
      let issueTracker = '';
      const issueTrackerJson = rowInfo.original.issuetracker;

      if (issueTrackerJson.whd) issueTracker = TOOL_NAME_SOLAR_WINDS;
      if (issueTrackerJson.gitlab) issueTracker = TOOL_NAME_GITLAB;
      if (issueTrackerJson.servicenow) issueTracker = TOOL_NAME_SERVICE_NOW;
      if (issueTrackerJson.mesd) issueTracker = TOOL_NAME_SERVICE_DESK;
      if (issueTrackerJson.jira) {
        return issueTrackerJson.jira.map((issue) => {
          const id = (issue.tool_id ?? issue.key)?.toUpperCase();

          return (
            <Link key={ id } target="_blank" href={ issue.url }>
              {id},{' '}
            </Link>
          );
        });
      }
      if (issueTrackerJson && issueTracker && issueTrackerJson[issueTracker].length) {
        return <Link target="_blank" href={ issueTrackerJson[issueTracker][0].url }>{issueTracker.toUpperCase()}</Link>;
      } else if (issueTrackerJson && issueTracker && issueTrackerJson[issueTracker]) {
        return <Link target="_blank" href={ issueTrackerJson[issueTracker].url }>{issueTracker.toUpperCase()}</Link>;
      }
      return null;
    },
    order: orderOf('issuetracker'),
    cellProps: minWdColorProps('issuetracker')
  }, {
    header: () => <div children="Updated" />,
    id: 'update_date',
    visible: (ctx) => ctx.fields.update_date?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('update_date', { groupAware: true }),
    cell: (rowInfo) => {
      const updateTime = get(rowInfo, 'original.metadata.update_time', null);
      if (!updateTime) return null;
      return <DateFormatter date={ updateTime } />;
    },
    cellProps: minWdColorProps('update_date'),
    order: orderOf('update_date')
  }, {
    header: (info, { fields }) => <div children="STATUS" className={ `min-wd-${fields.status?.defaultMin}` } />,
    id: 'status',
    accessor: 'status',
    visible: (ctx) => ctx.fields.status?.visible && !isDetailExtended(ctx) && (!ctx.groupBy || ctx.groupBy !== 'status'),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('status', { groupAware: true }),
    skeleton: false,
    cell: (rowInfo, ctx) => <StatusCell showSkeleton={ getShowSkeleton(ctx) } rowInfo={ rowInfo } attrib="status" capitalize />,
    cellProps: (rowInfo, { fields }) => (rowInfo.original.status === 'open' ? { 'className': `open min-wd-${fields.status?.defaultMin}`, 'column-type': 'status' } : { 'className': `${rowInfo.original.status} min-wd-${fields.status?.defaultMin}`, 'column-type': 'status' }),
    order: orderOf('status')
  }, {
    header: () => <div children="Last Detected" />,
    id: 'last_detected',
    visible: (ctx) => ctx.fields.last_detected?.visible && !isDetailExtended(ctx),
    sortable: notGrouped,
    resizable: notGrouped,
    ...fieldWidths('last_detected', { groupAware: true }),
    cell: (rowInfo) => (rowInfo.original.last_detected ? <DateFormatter date={ moment.utc(rowInfo.original?.last_detected).local() } /> : null),
    cellProps: minWdColorProps('last_detected'),
    order: orderOf('last_detected')
  }, {
    header: () => <div>TAGS</div>,
    accessor: 'tags',
    sortable: false,
    // Legacy bug preserved: minWidth reads maxWidth.
    minWidth: ({ fields }) => fields.tags?.maxWidth,
    maxWidth: ({ fields, groupBy }) => (groupBy ? fields.tags?.defaultMin : fields.tags?.maxWidth),
    resizable: notGrouped,
    id: 'tags',
    visible: (ctx) => ctx.fields.tags?.visible && !isDetailExtended(ctx) && ctx.isTagsAllowedByLicense,
    cell: (rowInfo) => <TagCell rowInfo={ rowInfo } />,
    cellProps: (rowInfo, { fields }) => ({ 'column-type': 'tags', 'className': `min-wd-${fields.tags?.defaultMin}` }),
    order: orderOf('tags')
  }
];

// Custom attribute columns, appended after the base ones (only the visible ones, as legacy did).
const getCustomFieldColumns = (ctx) => {
  const { customFields, fields } = ctx;
  const columns = [];

  if (customFields) {
    customFields.forEach((cf) => {
      const key = `${cf.field_display_name}_custom`;
      const defaultFieldMeta = fields[cf.field_name];
      const fieldMeta = fields[key];

      if (!fieldMeta) return;

      const showCustom = fieldMeta.visible && !isDetailExtended(ctx);
      const defaultVisible = defaultFieldMeta?.visible;

      if (showCustom && (!defaultVisible || (defaultVisible && showCustom))) {
        columns.push({
          header: () => <div>{cf.field_display_name.toUpperCase()}</div>,
          id: key,
          accessor: row => row.custom_fields?.[cf.field_name] ?? '',
          visible: true,
          sortable: false,
          resizable: false,
          maxWidth: 200,
          minWidth: 200,
          order: fieldMeta.order,
          cell: (rowInfo) => <p>{rowInfo.value}</p>
        });
      }
    });
  }

  return columns;
};

const getColumns = (ctx) => [...baseColumns, ...getCustomFieldColumns(ctx)];

// Cells that render their own skeleton (`showSkeleton` prop) or none opt out with `skeleton: false`.
const loading = { skeleton: getShowSkeleton };

/**
 * Main table (`isPivot: true`).
 */
const vulnsGeneralSchema = {
  id: 'vulnsGeneral',
  loading,
  ordering: {},
  normalizeWidths: true,
  columns: getColumns
};

/**
 * Group-by sub table (`isPivot: false`).
 */
export const vulnsGeneralSubTableSchema = {
  id: 'vulnsGeneralSubTable',
  styles: assetItemRowText,
  loading,
  ordering: {},
  normalizeWidths: true,
  columns: getColumns
};

export default vulnsGeneralSchema;
